import { Download, Loader2, MessageSquare, Trash } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CommentSidebar } from "@/components/Partnership/VideoFeedback/CommentSidebar";
import { ConvexError } from "convex/values";
import { Id } from "@cvx/_generated/dataModel";
import StatusBadge from "@/components/Generic/StatusBadge";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { Textarea } from "@/components/ui/textarea";
import { VIDEO_EVENTS } from "@/constants/analyticsEvents";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { format } from "date-fns";
import { useConvexMutation } from "@convex-dev/react-query";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useToast } from "@/components/ui/use-toast";

interface VideoProject {
  creatorName: string;
  projectTitle: string;
  status:
  | "draft"
  | "ai_reviewing"
  | "ai_approved"
  | "ai_rejected"
  | "team_reviewing"
  | "team_rejected"
  | "needs_review"
  | "brand_reviewing"
  | "approved"
  | "rejected";
  contractStatus: string;
  projectId: Id<"creatorVideoProjects">;
  creatorId: Id<"creators">;
  contractId: Id<"creatorContracts">;
  createdAt?: number;
  linkedVideoTitle?: string;
}

interface VideoProjectSidebarProps {
  project: VideoProject | null;
  isOpen: boolean;
  onClose: () => void;
  onRefetch: () => void;
  teamVideoReviewRequired?: boolean;
}

export function VideoProjectSidebar({
  project,
  isOpen,
  onClose,
  onRefetch,
  teamVideoReviewRequired = false,
}: VideoProjectSidebarProps) {
  const { toast } = useToast();
  const { track } = useMixpanel();
  const [rejectionReason, setRejectionReason] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    status:
    | "draft"
    | "approved"
    | "rejected"
    | "brand_reviewing"
    | "team_rejected"
    | "needs_review";
    comment?: string;
  } | null>(null);
  const [isDownloadingVersion, setIsDownloadingVersion] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");

  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));

  // State for active version - defaults to latest
  const [activeVersion, setActiveVersion] = useState<{
    id: Id<"creatorVideoVersion">;
    status: string;
    videoUrl: string;
  } | null>(null);

  // Determine account type based on user role and video status
  const accountType: "team" | "brand" = useMemo(() => {
    if (!user || !activeVersion) return "brand";

    const isTeamMember = TEAM_USER_IDS.includes(user._id);
    const isTeamReviewStage = activeVersion.status.includes("team_");

    // Use "team" account type if user is a team member AND status is team_reviewing
    if (isTeamMember && isTeamReviewStage) {
      return "team";
    }

    return "brand";
  }, [user, activeVersion?.status]);

  // Reset version-specific state when switching to a different project
  useEffect(() => {
    // Ensures the newly opened project defaults to its latest version
    setActiveVersion(null);
    setRejectionReason("");
    setShowAddComment(false);
    setNewCommentText("");
    setShowConfirmDialog(false);
    setPendingStatusChange(null);
  }, [project?.projectId]);

  // Fetch project details including versions
  const {
    data: projectDetails,
    isLoading: projectLoading,
    refetch: refetchProjectDetails,
  } = useQuery({
    ...convexQuery(
      api.creator_internal_videos.getPaginatedCreatorVideoProjects,
      {
        contractId: (project?.contractId || "") as Id<"creatorContracts">,
        accountType,
      },
    ),
    enabled: !!project?.contractId,
  });

  // Find the current project in the detailed data
  const currentProjectDetails = projectDetails?.find(
    (p) => p.projectId === project?.projectId,
  );

  // Update active version when project details load or change
  useEffect(() => {
    if (
      currentProjectDetails?.versions &&
      currentProjectDetails.versions.length > 0
    ) {
      if (!activeVersion) {
        // Set to latest version if no active version
        const latest =
          currentProjectDetails.versions[
          currentProjectDetails.versions.length - 1
          ];
        setActiveVersion(latest);
      } else {
        // Find and update the active version with new data (to get updated status)
        const updatedVersion = currentProjectDetails.versions.find(
          (v) => v.id === activeVersion.id,
        );
        if (updatedVersion) {
          setActiveVersion(updatedVersion);
        }
      }
    }
  }, [currentProjectDetails]);

  const { mutateAsync: updateVersionStatus } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.updateVersionStatus,
    ),
  });

  const { mutateAsync: addComment } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.addVersionComment,
    ),
  });

  const { mutateAsync: deleteComment } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.deleteVersionComment,
    ),
  });

  const { mutateAsync: editComment } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.editVersionComment,
    ),
  });

  const { mutateAsync: deleteProject } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.deleteCreatorVideoProject,
    ),
  });

  // Query for comments on the active version
  const { data: commentsResults, isPending: commentsLoading } = useQuery({
    ...convexQuery(api.creator_internal_videos.getVersionComments, {
      projectId: (project?.projectId || "") as Id<"creatorVideoProjects">,
      accountType,
      versionId: (activeVersion?.id || "") as Id<"creatorVideoVersion">,
    }),
    enabled: !!project?.projectId && !!activeVersion?.id,
  });

  // Query for app founders
  const { data: appFounders } = useQuery({
    ...convexQuery(api.creator_internal_videos.getAppFoundersByProjectId, {
      projectId: (project?.projectId || "") as Id<"creatorVideoProjects">,
    }),
    enabled: !!project?.projectId,
  });

  const handleStatusChangeClick = (
    newStatus:
      | "draft"
      | "approved"
      | "rejected"
      | "brand_reviewing"
      | "team_rejected"
      | "needs_review",
    comment?: string,
  ) => {
    // Show confirmation for Approve and Reject, but not for Needs Review
    if (
      newStatus === "approved" ||
      newStatus === "rejected" ||
      newStatus === "team_rejected" ||
      newStatus === "brand_reviewing"
    ) {
      setPendingStatusChange({ status: newStatus, comment });
      setShowConfirmDialog(true);
    } else {
      // Needs Review doesn't need confirmation
      handleStatusChange(newStatus, comment);
    }
  };

  const handleStatusChange = async (
    newStatus:
      | "draft"
      | "approved"
      | "rejected"
      | "brand_reviewing"
      | "team_rejected"
      | "needs_review",
    comment?: string,
  ) => {
    if (!project || !activeVersion) return;

    try {
      // If there's a comment with the status change, add it first
      if (comment && comment.trim()) {
        await addComment({
          versionId: activeVersion.id,
          accountType,
          message: comment.trim(),
          timestampRange: {
            start: 0,
            end: undefined,
          },
        });
      }

      await updateVersionStatus({
        projectId: project.projectId,
        versionId: activeVersion.id,
        status: newStatus,
        accountType,
      });

      // Track video approval by brand/founder
      if (newStatus === "approved") {
        track(VIDEO_EVENTS.VIDEO_APPROVED, {
          user_id: user?._id,
          project_id: project.projectId,
          version_id: activeVersion.id,
          contract_id: project.contractId,
          creator_id: project.creatorId,
          project_title: project.projectTitle,
          approval_type: "brand",
          previous_status: project.status,
          timestamp: new Date().toISOString(),
        });
      } else if (newStatus === "brand_reviewing") {
        // Track team approval (sends to brand review)
        track(VIDEO_EVENTS.VIDEO_APPROVED, {
          user_id: user?._id,
          project_id: project.projectId,
          version_id: activeVersion.id,
          contract_id: project.contractId,
          creator_id: project.creatorId,
          project_title: project.projectTitle,
          approval_type: "team",
          previous_status: project.status,
          timestamp: new Date().toISOString(),
        });
      }

      toast({
        title: "Status updated",
        description: `Video status has been changed to ${newStatus.replace("_", " ")}`,
      });

      // Reset rejection reason
      setRejectionReason("");

      // Force refetch of both dashboard data and project details
      onRefetch();
      refetchProjectDetails();
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error updating status",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error updating the video status",
        variant: "destructive",
      });
    }
  };

  const handleConfirmStatusChange = () => {
    if (pendingStatusChange) {
      handleStatusChange(
        pendingStatusChange.status,
        pendingStatusChange.comment,
      );
      setShowConfirmDialog(false);
      setPendingStatusChange(null);
    }
  };

  const handleDownloadActiveVersion = async () => {
    if (!activeVersion?.videoUrl || isDownloadingVersion || !project) return;

    setIsDownloadingVersion(true);

    try {
      // Create download link directly from video URL
      const versionNumber =
        (currentProjectDetails?.versions.findIndex(
          (v) => v.id === activeVersion.id,
        ) ?? -1) + 1;
      const link = document.createElement("a");
      link.href = activeVersion.videoUrl;
      link.download = `${project.projectTitle}_v${versionNumber}.mp4`;
      link.target = "_blank"; // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading video:", error);
      toast({
        title: "Error downloading video",
        description: "There was an error downloading the video",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingVersion(false);
    }
  };

  const handleReply = async (
    commentId: Id<"creatorVideoComments">,
    content: string,
    attachments?: Array<{
      storageId: Id<"_storage">;
      name: string;
      type: string;
      size: number;
    }>,
  ) => {
    if (!activeVersion) return;

    try {
      await addComment({
        versionId: activeVersion.id,
        accountType,
        message: content,
        replyTo: commentId,
        timestampRange: {
          start: 0,
          end: 0,
        },
        attachments,
      });
      toast({
        title: "Reply added",
        description: "Your reply has been added",
      });
      refetchProjectDetails();
    } catch (error) {
      console.error("Error adding reply:", error);
      toast({
        title: "Error adding reply",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error adding your reply",
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (commentId: Id<"creatorVideoComments">) => {
    try {
      await deleteComment({
        commentId,
        accountType,
      });
      toast({
        title: "Comment deleted",
        description: "Your comment has been deleted",
      });
      refetchProjectDetails();
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: "Error deleting comment",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error deleting your comment",
        variant: "destructive",
      });
    }
  };

  const handleEditComment = async (
    commentId: Id<"creatorVideoComments">,
    content: string,
  ) => {
    try {
      await editComment({
        commentId,
        message: content,
        accountType,
      });
      toast({
        title: "Comment updated",
        description: "Your comment has been updated",
      });
      refetchProjectDetails();
    } catch (error) {
      console.error("Error editing comment:", error);
      toast({
        title: "Error editing comment",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error editing your comment",
        variant: "destructive",
      });
    }
  };

  const handleAddNewComment = async () => {
    if (!activeVersion || !newCommentText.trim()) return;

    try {
      await addComment({
        versionId: activeVersion.id,
        accountType,
        message: newCommentText.trim(),
        timestampRange: {
          start: 0,
          end: undefined,
        },
      });

      toast({
        title: "Comment added",
        description: "Your comment has been added",
      });

      setNewCommentText("");
      setShowAddComment(false);
      refetchProjectDetails();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error adding comment",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error adding your comment",
        variant: "destructive",
      });
    }
  };

  const handleDeleteProject = async () => {
    if (!project) return;

    if (
      !window.confirm(
        `Are you sure you want to delete the project "${project.projectTitle}"? This action cannot be undone and will delete all versions and comments.`,
      )
    ) {
      return;
    }

    try {
      await deleteProject({
        projectId: project.projectId,
        accountType,
      });

      toast({
        title: "Project deleted",
        description: "The project has been deleted successfully",
      });

      // Close the sidebar and refetch dashboard data
      onClose();
      onRefetch();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast({
        title: "Error deleting project",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error deleting the project",
        variant: "destructive",
      });
    }
  };

  // Filter comments based on user role and teamVideoReviewRequired
  const filteredComments = useMemo(() => {
    if (!commentsResults?.comments || !user)
      return commentsResults?.comments || [];

    const isTeamMember = TEAM_USER_IDS.includes(user._id);

    // Team members always see everything
    if (isTeamMember) {
      return commentsResults.comments;
    }

    // If teamVideoReviewRequired is false, brand accounts see everything
    if (!teamVideoReviewRequired) {
      return commentsResults.comments;
    }

    // If teamVideoReviewRequired is true, brand accounts should only see:
    // 1. Their own comments
    // 2. Comments/replies posted AFTER their first initial comment

    // Find the brand user's first comment
    const brandUserFirstComment = commentsResults.comments.find((comment) => {
      const authorId =
        typeof comment.author === "object"
          ? comment.author._id
          : comment.author;
      return authorId === user._id;
    });

    // If they haven't commented yet, they see nothing
    if (!brandUserFirstComment) {
      return [];
    }

    const firstCommentTime = brandUserFirstComment._creationTime;

    // Filter: show user's own comments OR comments after their first comment
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filterCommentsRecursively = (comments: any[]): any[] => {
      return comments
        .filter((comment) => {
          const authorId =
            typeof comment.author === "object"
              ? comment.author._id
              : comment.author;
          return (
            authorId === user._id || comment._creationTime > firstCommentTime
          );
        })
        .map((comment) => ({
          ...comment,
          replies: comment.replies
            ? filterCommentsRecursively(comment.replies)
            : [],
        }));
    };

    return filterCommentsRecursively(commentsResults.comments);
  }, [commentsResults?.comments, user, teamVideoReviewRequired]);

  if (!project) return null;

  return (
    <>
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
            <DialogDescription>
              {pendingStatusChange?.status === "approved" ||
                pendingStatusChange?.status === "brand_reviewing"
                ? "Are you sure you want to approve this video? This will notify the creator/brand."
                : "Are you sure you want to reject this video? This will notify the creator/brand."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDialog(false);
                setPendingStatusChange(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmStatusChange}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[90vw] max-w-[1800px] sm:max-w-[1800px] p-0">
          <div className="h-full overflow-hidden">
            {projectLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="grid grid-cols-[1.2fr,0.8fr] gap-0 h-full">
                {/* Left Side - Video Preview (Full Height) */}
                <div className="bg-black flex items-center justify-center h-screen">
                  {activeVersion?.videoUrl && (
                    <video
                      src={activeVersion.videoUrl}
                      controls
                      className="w-full h-full object-contain"
                      preload="metadata"
                    />
                  )}
                </div>

                {/* Right Side - Information and Actions */}
                <div className="overflow-y-auto p-6 space-y-6 h-screen">
                  {/* Header with Download and Delete Buttons */}
                  <div className="flex items-center justify-between pb-4 border-b border-border">
                    <h3 className="font-medium text-primary text-lg">
                      Video Details
                    </h3>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteProject}
                        title="Delete project"
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadActiveVersion}
                        disabled={isDownloadingVersion || !activeVersion}
                        title={
                          isDownloadingVersion
                            ? "Downloading..."
                            : `Download version ${(currentProjectDetails?.versions.findIndex(
                              (v) => v.id === activeVersion?.id,
                            ) ?? -1) + 1
                            }`
                        }
                      >
                        {isDownloadingVersion ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Version Selector */}
                  {currentProjectDetails &&
                    currentProjectDetails.versions.length > 1 && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-primary/60">
                          Select Version
                        </label>
                        <div className="flex gap-2 flex-wrap">
                          {currentProjectDetails.versions.map(
                            (version, index) => (
                              <Button
                                key={version.id}
                                size="sm"
                                variant={
                                  version.id === activeVersion?.id
                                    ? "default"
                                    : "outline"
                                }
                                onClick={() => setActiveVersion(version)}
                              >
                                v{index + 1}
                              </Button>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                  {/* Project Information */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-primary text-lg">
                      Information
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-primary/60">
                          Video Status
                        </label>
                        <div className="mt-1">
                          <StatusBadge
                            status={activeVersion?.status || project.status}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-primary/60">
                          Campaign Status
                        </label>
                        <div className="mt-1">
                          <StatusBadge status={project.contractStatus} />
                        </div>
                      </div>
                    </div>

                    {project.createdAt && (
                      <div>
                        <label className="text-sm font-medium text-primary/60">
                          Created
                        </label>
                        <p className="mt-1 text-sm text-primary">
                          {format(
                            new Date(project.createdAt),
                            "MMM dd, yyyy 'at' HH:mm",
                          )}
                        </p>
                      </div>
                    )}

                    {project.linkedVideoTitle && (
                      <div>
                        <label className="text-sm font-medium text-primary/60">
                          Linked Published Video
                        </label>
                        <p className="mt-1 text-sm text-primary">
                          {project.linkedVideoTitle}
                        </p>
                      </div>
                    )}

                    {currentProjectDetails && (
                      <div>
                        <label className="text-sm font-medium text-primary/60">
                          Versions
                        </label>
                        <p className="mt-1 text-sm text-primary">
                          {currentProjectDetails.versions.length} version
                          {currentProjectDetails.versions.length !== 1
                            ? "s"
                            : ""}{" "}
                          uploaded
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Status Management */}
                  {activeVersion &&
                    (activeVersion.status?.includes("reviewing") ||
                      activeVersion.status === "ai_approved") && (
                      <div className="space-y-4 pt-4 border-t border-border">
                        <h3 className="font-medium text-primary">Review Actions</h3>

                        <div className="flex flex-col gap-2">
                          <div className="flex gap-2">
                            {/* Approve button */}
                            {accountType === "team" &&
                              activeVersion.status === "team_reviewing" && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() =>
                                    handleStatusChangeClick("brand_reviewing")
                                  }
                                >
                                  Approve
                                </Button>
                              )}
                            {accountType === "brand" &&
                              activeVersion.status === "brand_reviewing" &&
                              !currentProjectDetails?.versions.some(
                                (v) =>
                                  v.status === "approved" &&
                                  v.id !== activeVersion?.id,
                              ) && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleStatusChangeClick("approved")}
                                >
                                  Approve
                                </Button>
                              )}

                            {/* Needs Review button */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChangeClick("needs_review")}
                            >
                              Needs Review
                            </Button>

                            {/* Reject button */}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() =>
                                handleStatusChangeClick(
                                  accountType === "team"
                                    ? "team_rejected"
                                    : "rejected",
                                  rejectionReason || undefined,
                                )
                              }
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Comments Section */}
                  {activeVersion && (
                    <div className="pt-4 border-t border-border">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          <h3 className="font-medium text-primary">
                            Comments & Feedback
                          </h3>
                        </div>
                        {!showAddComment && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowAddComment(true)}
                          >
                            Add Comment
                          </Button>
                        )}
                      </div>

                      {/* Add Comment Form */}
                      {showAddComment && (
                        <Card className="p-4 mb-4">
                          <div className="space-y-3">
                            <Textarea
                              placeholder="Write your comment..."
                              value={newCommentText}
                              onChange={(e) => setNewCommentText(e.target.value)}
                              className="min-h-[80px]"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleAddNewComment}
                                disabled={!newCommentText.trim()}
                              >
                                Add Comment
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setShowAddComment(false);
                                  setNewCommentText("");
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </Card>
                      )}

                      {commentsLoading ? (
                        <Card className="p-4">
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-6 w-6 animate-spin" />
                          </div>
                        </Card>
                      ) : (
                        <div className="space-y-4">
                          <CommentSidebar
                            comments={filteredComments}
                            appFounders={appFounders ?? []}
                            onReply={handleReply}
                            onSeekToComment={() => { }} // Video seeking not needed in sidebar
                            onDeleteComment={handleDeleteComment}
                            onEditComment={handleEditComment}
                            currentUser={user ?? undefined}
                            isLoading={commentsLoading}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
